import { Button, Icon, Unifree, YesObject } from '@yes.technology/react-toolkit'
import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import AudioStreamCapture from 'modules/shared/AudioStreamCapture'
import useDocument from 'modules/shared/hooks/useDocument'
import useModal from 'modules/shared/hooks/useModal'
import usePermission from 'modules/shared/hooks/usePermission'
import PermissionDeniedCard from 'modules/shared/PermissionDeniedCard'
import FileUploader from 'modules/shared/FileUploader'
import useUploadS3, {
  UploadFileResult
} from 'modules/interaction/hooks/useUploadS3/useUploadS3'
import { useAppDispatch } from 'state/store'
import { startLoading, stopLoading } from 'modules/interaction/redux'
import PlayerVisualizer from 'modules/shared/Visualizer/PlayerVisualizer'
import useAudioBlobURL from './useAudioBlobURL'
import ActivityWithActions from '../ActivityWithActions/ActivityWithActions'
import { SELECT_AND_LINK_ACTION } from 'modules/interaction/hooks/useActions'
import { useSelectAndLinkFrameContext } from '../../SelectAndLinkFrame/SelectAndLinkFrameContext'
import { useBlueprintsContext } from 'modules/interaction/hooks'

type ActivityDocumentAudioProps = {
  uuid?: string
  inputValue: string
  required?: boolean
  onChange: (value: string) => void
  label: string
  actions?: string[]
  maxSizeInBytes?: number
  accept?: string
  hideDescription?: boolean
}

type Values = {
  uuid?: string
  uuid_status?: string
  statusDes?: string
  contentType?: string
  size?: string
  url?: string
  des?: string
  file?: string
}

const ActivityDocumentAudio = ({
  uuid,
  inputValue,
  onChange,
  label,
  required,
  accept = 'audio/*',
  maxSizeInBytes,
  actions = [],
  hideDescription
}: ActivityDocumentAudioProps) => {
  const { t } = useTranslation()
  const { createOrUpdateDocument } = useDocument()
  const { isOpenModal, openModal, closeModal } = useModal()
  const { permissionDenied, requestAccess, clearPermissionDenied } =
    usePermission('microphone')
  const { uploadFile } = useUploadS3()
  const { setSelectAndLinkData } = useSelectAndLinkFrameContext()

  const dispatch = useAppDispatch()
  const { executeBlueprints } = useBlueprintsContext()

  const [values, setValues] = useState<Values>()

  const audioBlobURL = useAudioBlobURL(values?.url)

  useEffect(() => {
    if (inputValue !== '') {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  const startLoadingPage = () => {
    dispatch(
      startLoading({
        des: t('file-uploader.loading.des'),
        complement: t('file-uploader.loading.complement')
      })
    )
  }

  const stopLoadingPage = () => {
    dispatch(stopLoading())
  }

  const onSuccessUploadFile = async ({ url, content }: UploadFileResult) => {
    const currentValues = inputValue && JSON.parse(inputValue)

    const { document, statusDes, statusUuid } = await createOrUpdateDocument({
      uuid: currentValues?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    onChange(
      JSON.stringify({
        ...currentValues,
        uuid: document?.uuid,
        uuid_status: statusUuid,
        statusDes: statusDes,
        des: document?.des,
        contentType: content.type,
        size: content.size,
        url,
        file: url
      })
    )

    stopLoadingPage()
  }

  const captureAudio = async (audioUrl: string) => {
    startLoadingPage()
    closeModal()

    try {
      const blob = await fetch(audioUrl).then((r) => r.blob())
      URL.revokeObjectURL(audioUrl)
      const file = new File([blob], `recording-${Date.now()}.webm`, {
        type: 'audio/webm'
      })
      uploadFile({ file }).then(onSuccessUploadFile).catch(stopLoadingPage)
    } catch {
      URL.revokeObjectURL(audioUrl)
      stopLoadingPage()
    }
  }

  const onChangeDescription = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const saveDescription = (event: FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !values?.file) {
      return
    }

    setTimeout(async () => {
      const parsedValues = JSON.parse(inputValue)
      const { document, statusDes, statusUuid } = await createOrUpdateDocument(
        {
          uuid: parsedValues.uuid,
          des: event.target.value
        },
        false
      )

      onChange(
        JSON.stringify({
          ...parsedValues,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusUuid,
          statusDes: statusDes
        })
      )
    }, 500)
  }

  const uploadLocalFile = (file: File) => {
    startLoadingPage()

    uploadFile({
      file
    })
      .then(onSuccessUploadFile)
      .catch(stopLoadingPage)
  }

  const clearData = () => {
    audioBlobURL && URL.revokeObjectURL(audioBlobURL)
    onChange('')
    setValues({})
  }

  const handleMicrophoneClick = async () => {
    const granted = await requestAccess()
    if (granted) openModal()
  }

  const handleActionClick = (action: string, uniselection?: boolean) => {
    if (action === SELECT_AND_LINK_ACTION) {
      setSelectAndLinkData({
        uuidObjectclass: window.documentObjectclassUuid,
        callback: async (values) => {
          const object: YesObject = values?.[0]
          if (object) {
            const { statusDes, statusUuid } = await createOrUpdateDocument({
              uuid: object.uuid
            })

            if (statusDes) {
              const value = JSON.stringify({
                uuid: object.uuid,
                uuid_status: statusUuid,
                statusDes: statusDes,
                des: object.des,
                contentType: object.content_type
              })

              onChange(value)

              executeBlueprints({
                type: 'activity_changed',
                activityValue: value,
                activityModelUuid: uuid
              })
            }
          }
        },
        uniselection
      })
    }
  }

  return (
    <>
      {!isOpenModal && (
        <div style={{ display: 'grid', rowGap: '8px' }}>
          {!hideDescription && (
            <Unifree
              label={t('activity-document-audio.labels.des')}
              value={values?.des || ''}
              onChange={onChangeDescription}
              onBlur={saveDescription}
              data-testid='description-field'
              disabled={!values?.file}
              placeholder={t('activity-document-audio.placeholders.des')}
            />
          )}
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1' style={{ marginRight: '8px' }}>
              <ActivityWithActions
                {...{ actions, onClickAction: handleActionClick }}
              >
                <Unifree
                  label={
                    label || t('activity-document-audio.labels.uuid-audio')
                  }
                  disabled={true}
                  value={values?.uuid || ''}
                  required={required}
                  placeholder={t(
                    'activity-document-audio.placeholders.uuid-audio'
                  )}
                />
              </ActivityWithActions>
            </div>
            {values?.file ? (
              <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                <Button
                  variant='secondary'
                  onClick={clearData}
                  style={{ width: '40px' }}
                  data-testid='clear-button'
                >
                  <Icon iconName='Cleaner' icSize='semiSmall' />
                </Button>
              </div>
            ) : (
              <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                <Button
                  onClick={handleMicrophoneClick}
                  style={{ width: '40px' }}
                  data-testid='record-button'
                >
                  <Icon iconName='TakeAudio' icSize='semiSmall' />
                </Button>
                <FileUploader
                  onChange={uploadLocalFile}
                  accept={accept}
                  maxSizeInBytes={maxSizeInBytes}
                  variant='secondary'
                  style={{ width: '40px' }}
                />
              </div>
            )}
          </div>
          <Unifree
            label={t('activity-document-audio.labels.status')}
            disabled={true}
            value={values?.statusDes || ''}
            required={required}
          />
          <div className='row'>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-audio.labels.content-type')}
                disabled={true}
                value={values?.contentType || ''}
                required={required}
                placeholder={t(
                  'activity-document-audio.placeholders.content-type'
                )}
              />
            </div>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-audio.labels.size')}
                disabled={true}
                value={values?.size ? formatFileSize(Number(values.size)) : ''}
                required={required}
                placeholder={t('activity-document-audio.placeholders.size')}
              />
            </div>
          </div>
          <div className='w-100 d-flex justify-content-center align-items-center'>
            {audioBlobURL ? (
              <PlayerVisualizer url={audioBlobURL} variant='secondary' />
            ) : (
              <div
                className='w-100 webcam-no-image rounded'
                data-testid='no-audio-placeholder'
              >
                {t('activity-document-audio.no-audio')}
              </div>
            )}
          </div>
        </div>
      )}
      {isOpenModal && (
        <AudioStreamCapture onSuccess={captureAudio} onCancel={closeModal} />
      )}
      <PermissionDeniedCard
        isOpen={permissionDenied}
        onClose={clearPermissionDenied}
        title={t('permission.denied.microphone-title')}
      />
    </>
  )
}

export default ActivityDocumentAudio
